import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-relay';

import { checkAssigneeData } from '../functions';
import { TaskTypeInput } from '../graphql/__generated__/UpdateTaskMutation.graphql';
import UpdateTaskMutation from '../graphql/mutations/UpdateTaskMutation';
import useCommitTask from '../hooks/useCommitTask';
import useObserverComponent from '../hooks/useObserverComponent';
import { RootState } from '../store';
import { setterTaskBoardDraggable } from '../store/slices/taskBoardDraggableSlice';
import { setterTaskBoard } from '../store/slices/taskBoardSlice';
import { TaskBoardColumnHeaders } from './component/taskBoardColumnHeaders';
import { TaskBoardColumns } from './component/taskBoardColumns';
import { BACKLOG_ID } from './component/taskBoardColumnsLoader';
import { useScrollWhenDragging } from './hook/useScrollWhenDragging';
import { TaskBoardColumnsType, TaskBoardDropType, TaskBoardTaskType } from './taskBoardTypes';

interface TaskBoardProps {
    paddingBottom?: number;
    marginTop?: number;
    columns: TaskBoardColumnsType;
}

export const TaskBoard: React.FC<TaskBoardProps> = ({ paddingBottom = 0, marginTop, columns }) => {
    const [commitTaskUpdate] = useMutation(UpdateTaskMutation);
    const { getCommitTask } = useCommitTask('day');
    const dispatch = useDispatch();

    const { ref: containerParent } = useObserverComponent<HTMLDivElement>();
    const onDragOver = useScrollWhenDragging(containerParent);

    const connectionTaskId = useSelector<RootState, string>((state) => state.calendar.connectionTaskId);
    const drop = useSelector<RootState, TaskBoardDropType | null>((state) => state.taskBoardDraggable.drop);
    const draggingTask = useSelector<RootState, TaskBoardTaskType | null>(
        (state) => state.taskBoardDraggable.draggingTask,
    );

    useEffect(() => {
        if (drop && draggingTask) {
            const { dropAreaId, dropColumnId } = drop;
            const dropColumn = columns[dropColumnId];
            const dropArea = dropColumn?.dropAreas.find((area) => area.id === dropAreaId);

            const currentColumn = columns[draggingTask.columnId];
            if (dropArea && currentColumn && currentColumn !== dropColumn) {
                const currentColumnTasks = currentColumn.tasks.filter(
                    (columnTask) => columnTask.id !== draggingTask.id,
                );
                const dropColumnTasks = [
                    ...dropColumn.tasks,
                    {
                        ...draggingTask,
                        columnId: dropColumn.id,
                        status: dropArea.name,
                    },
                ];

                commitTaskUpdate(
                    getCommitTask(
                        {
                            ...draggingTask.dataFromServer,
                            status: dropColumn.id !== BACKLOG_ID ? dropColumn.id : null,
                            sub_status: dropArea.id !== BACKLOG_ID ? dropArea.id : null,
                            assignee_for_task: checkAssigneeData(draggingTask.dataFromServer.assignee_for_task).join(
                                ',',
                            ),
                        } as TaskTypeInput,
                        connectionTaskId,
                        draggingTask.id,
                    ),
                );

                dispatch(
                    setterTaskBoard({
                        columns: JSON.parse(
                            JSON.stringify({
                                ...columns,
                                [currentColumn.id]: {
                                    ...currentColumn,
                                    tasks: currentColumnTasks,
                                },
                                [dropColumn.id]: {
                                    ...dropColumn,
                                    tasks: dropColumnTasks,
                                },
                            }),
                        ),
                    }),
                );
            }

            dispatch(
                setterTaskBoardDraggable({
                    drop: null,
                    draggingTask: null,
                }),
            );
        }
    }, [drop, draggingTask]);

    return (
        <div
            style={{
                overflowX: 'hidden',
                height: `calc(100% - ${paddingBottom}px - ${marginTop}px)`,
                overflowY: 'auto',
                marginTop,
                minHeight: 400,
            }}
            onDragOver={onDragOver}
        >
            <TaskBoardColumnHeaders columns={columns} />
            <TaskBoardColumns columns={columns} />
        </div>
    );
};
